import { motion } from 'framer-motion';
import { formatReais } from '@/lib/utils';

interface ROIHeroProps {
  roi: number;
  invested: number;
  influenced: number;
  budgetUsed: number;
  budgetTotal: number;
}

function getRingColor(pct: number): string {
  if (pct < 60) return 'var(--color-success)';
  if (pct < 85) return 'var(--color-warning)';
  return 'var(--color-destructive)';
}

export function ROIHero({ roi, invested, influenced, budgetUsed, budgetTotal }: ROIHeroProps) {
  const pct = budgetTotal > 0 ? Math.min((budgetUsed / budgetTotal) * 100, 100) : 0;
  const ringColor = getRingColor(pct);

  // SVG ring geometry
  const size = 180;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct / 100);

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-8">
        {/* Ring */}
        <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="-rotate-90">
            {/* Track */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              className="text-muted-foreground/15"
            />
            {/* Progress */}
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: dashOffset }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </svg>
          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span
              className="text-3xl font-bold"
              style={{ color: 'var(--color-primary)' }}
            >
              {roi}x
            </span>
            <span className="text-xs text-muted-foreground">ROI</span>
          </div>
        </div>

        {/* Details */}
        <div className="flex flex-col gap-2 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">
            {formatReais(invested)} investido{' '}
            <span className="mx-1">&rarr;</span>{' '}
            {formatReais(influenced)} influenciado
          </p>
          <p className="text-sm font-medium">
            {formatReais(budgetUsed)} de {formatReais(budgetTotal)}{' '}
            <span className="text-muted-foreground">
              ({Math.round(pct)}%)
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            Consumo do orçamento no período
          </p>
        </div>
      </div>
    </div>
  );
}
